import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { socket } from "../App";
import { t } from "../i18n";
import messageIcon from "../assets/comment.svg";
import "./Friends.css"; // Ensure you have this file

const FriendList = ({ connections, loadFriends, setConnections, isLoaded, error }) => {
 const navigate = useNavigate();
 const currentUser = parseInt(sessionStorage.getItem("user"));
 const [friendPics, setFriendPics] = useState({}); // Store the profile pictures for each friend

 useEffect(() => {
   loadFriends();
 }, [loadFriends]);

 useEffect(() => {
   // Fetch profile pictures for all friends
   const fetchFriendPictures = async () => {
     const friendPicsData = {};

     // Fetch picture for each friend (both toUser and fromUser in connection)
     for (const conn of connections) {
       const other = conn.toUser.id === currentUser ? conn.fromUser : conn.toUser;
       const userId = other.id;

       if (!friendPicsData[userId]) {
         try {
           const response = await fetch(`${process.env.REACT_APP_API_PATH}/users/${userId}`, {
             method: "GET",
             headers: {
               "Content-Type": "application/json",
               Authorization: `Bearer ${sessionStorage.getItem("token")}`,
             },
           });
           const result = await response.json();

           if (result.attributes && result.attributes.picture) {
             friendPicsData[userId] = result.attributes.picture;
           } else {
             friendPicsData[userId] = require("../assets/Profile-Icon.png"); // Use placeholder if no picture
           }
         } catch (error) {
           console.log("Error fetching friend profile picture", error);
           friendPicsData[userId] = require("../assets/Profile-Icon.png"); // Use placeholder on error
         }
       }
     }

     setFriendPics(friendPicsData);
   };

   if (connections.length > 0) {
     fetchFriendPictures();
   }
 }, [connections, currentUser]);

 const updateConnection = (id, status) => {
  fetch(`${process.env.REACT_APP_API_PATH}/connections/${id}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + sessionStorage.getItem("token"),
    },
    body: JSON.stringify({
      attributes: { status: status, type: "friend" },
    }),
  })
    .then((res) => res.json())
    .then(() => {
      // Handle rejection by removing rejected connections immediately
      if (status === "rejected") {
        deleteConnection(id); // Immediately remove rejected connection
      } else {
        loadFriends(); // Reload connections to reflect any changes
      }
    })
    .catch((error) => console.error("Error updating connection:", error));
};

const deleteConnection = (id) => {
  fetch(`${process.env.REACT_APP_API_PATH}/connections/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: "Bearer " + sessionStorage.getItem("token"),
    },
  })
    .then(() => {
      // Remove the rejected connection from the list in the UI
      setConnections(connections.filter((conn) => conn.id !== id)); // Remove from UI immediately
      loadFriends(); // Refresh the list of connections
    })
    .catch((error) => console.error("Error deleting connection:", error));
};

 const handleMessageClick = (connectionUser) => {
   socket.emit("/chat/join-room", {
     fromUserID: currentUser,
     toUserID: connectionUser.id,
   });

   socket.once("/room-created", (data) => {
     if (data?.roomID) {
       sessionStorage.setItem("toUserID", connectionUser.id);
       sessionStorage.setItem("roomID", data.roomID);
       navigate(`/messages/${data.roomID}`);
     }
   });
 };

 const renderConnectionActions = (conn) => {
   const isSender = conn.fromUser.id === currentUser;
   const isReceiver = conn.toUser.id === currentUser;
   const status = conn.attributes.status;
   const other = conn.toUser.id === currentUser ? conn.fromUser : conn.toUser;

   if (status === "pending") {
     if (isReceiver) {
       return (
         <>
           <button onClick={() => updateConnection(conn.id, "active")}>{t("accept")}</button>
           <button onClick={() => updateConnection(conn.id, "rejected")}>{t("reject")}</button>
         </>
       );
     } else if (isSender) {
       return <button onClick={() => updateConnection(conn.id, "canceled")}>{t("cancel")}</button>;
     }
   }

   if (status === "active") {
     return (
       <>
         <button className="icon-button" onClick={() => handleMessageClick(other)} title={t("message")}>
           <img src={messageIcon} alt={t("message")} className="icon-img" />
         </button>
         <button onClick={() => updateConnection(conn.id, "blocked")}>{t("block")}</button>
         <button className="danger-button" onClick={() => deleteConnection(conn.id)}>
           {t("unfriend")}
         </button>
       </>
     );
   }

   if (status === "rejected") return <i>{t("rejected")}</i>;
   if (status === "canceled") return <i>{t("canceled")}</i>;
   if (status === "blocked") {
     return <button onClick={() => updateConnection(conn.id, "active")}>{t("unblock")}</button>;
   }

   return null;
 };

 // Filter duplicates
 const uniqueConnections = [];
 const seenPairs = new Set();

 connections.forEach((conn) => {
   const userA = conn.fromUser.id;
   const userB = conn.toUser.id;
   const key = [userA, userB].sort().join("-");
   if (!seenPairs.has(key)) {
     seenPairs.add(key);
     uniqueConnections.push(conn);
   }
 });

 if (error) return <div>Error: {error.message}</div>;
 if (!isLoaded) return <div>{t("loading_friends")}</div>;

 return (
   <div className="friend-list-container">
     <h2 className="friend-list-title">{t("friends")}</h2>
     <div className="friends-grid">
       {uniqueConnections.slice().reverse().map((conn) => {
         const other = conn.toUser.id === currentUser ? conn.fromUser : conn.toUser;
         const username = other.attributes.username;
         const avatar = friendPics[other.id] || require("../assets/Profile-Icon.png"); // Fetch friend's profile picture, or use placeholder
         const status = conn.attributes.status;

         return (
           <div key={conn.id} className="friend-card">
             <img src={avatar} alt={`${username}'s avatar`} className="friend-avatar" />
             <div className="friend-info">
               <div className="friend-name">{username}</div>
               <div className="friend-status">({status})</div>
               <div className="friend-actions">{renderConnectionActions(conn)}</div>
             </div>
           </div>
         );
       })}
     </div>
   </div>
 );
};

export default FriendList;